import * as PIXI from 'pixi.js';
import Balance from 'dt-common/constants/Balance';
import getAvailableAttributePoints from 'dt-common/isomorphic-helpers/getAvailableAttributePoints';
import getUnitState from 'dt-common/isomorphic-helpers/getUnitState';
import Audio from '~/Audio';
import Tooltip from '~/components/tooltips/Tooltip';
import Colors from '~/constants/Colors';
import { EditHeroActions } from '~/flux/actions';
import { FluxGetters, HeroBuildStore, UIStore } from '~/flux/stores';
import text from '~/text';
import CanvasTools from '~/view/CanvasTools';
import AttPointsPanel from './AttPointsPanel';

const EditAttributes = function () {
  PIXI.Container.call(this);

  const { built_hero, hero_handle, roster_hero } = FluxGetters.getFocusedHeroBuild();
  let _hero_state = getUnitState({
    roster_hero,
    unit_build: built_hero,
  });

  let _available_att_points = getAvailableAttributePoints({
    built_hero,
    roster_hero,
  });

  let _primaryAttFields = [];
  let _secondaryAttFields = [];
  let _attPointsPanel;

  this.transitionIn = (duration) => {
    TweenMax.from(this, duration, { x: window.innerWidth });
  };

  this.transitionOut = (duration, onComplete) => {
    TweenMax.to(this, duration, {
      x: -window.innerWidth,
      onComplete,
    });
  };

  // compatability with legacy where there were "InfoPanels", not Tooltips
  this.hidePopups = () => {};

  this.dispose = () => {
    HeroBuildStore.removeListener(HeroBuildStore.ATTRIBUTE_CHANGE, onAttributeChange);
    UIStore.removeListener(UIStore.GAME_SUBMODE_SELECTION, onAttributeChange);

    destroyPrimaryAttributeFields();
    destroySecondaryAttributeFields();
    destroyAttPointsPanel();

    if (_tooltip) {
      _tooltip.dispose();
      _tooltip = null;
    }

    DT_CANVAS_GLOBALS.poolSafeDestroy(this, { children: true });
  };

  const TITLE_STYLE = {
    fontFamily: 'Courier New',
    fontSize: CanvasTools.dynamicFontSizeString(18),
    fontWeight: 'bold',
    fill: Colors.BRIGHT_YELLOW,
  };

  let _tooltip;

  const SECONDARY_ATT_TAGS = [
    'meleeDamage',
    'armorClass',
    'dodgeChance',
    'physicalResil',
    'mentalResil',
    'regeneration',
    'speed',
    'magicalGravity',
    'max_hp',
    'max_mp',
  ];

  function formatSecondaryValue(attTag, value) {
    switch (attTag) {
      case 'meleeDamage':
      case 'armorClass':
      case 'max_hp':
      case 'max_mp':
        return Number(value).toFixed(0);
      default: {
        let v = Number(value).toFixed(1);
        if (['dodgeChance', 'physicalResil', 'mentalResil'].includes(attTag)) {
          v += '%';
        }
        return v;
      }
    }
  }

  function formatDelta(attTag, delta) {
    const num = Number(delta);
    if (!Number.isFinite(num) || Math.abs(num) < 0.0001) return '+0';
    const abs = Math.abs(num);
    const sign = num > 0 ? '+' : '-';
    const digits = ['max_hp', 'max_mp', 'meleeDamage', 'armorClass'].includes(attTag) ? 0 : 1;
    let s = abs.toFixed(digits);
    if (['dodgeChance', 'physicalResil', 'mentalResil'].includes(attTag)) {
      s += '%';
    }
    return `${sign}${s}`;
  }

  function computeSecondaryChanges(primaryAttTag, direction) {
    const { built_hero, roster_hero } = FluxGetters.getFocusedHeroBuild();
    const beforeState = getUnitState({ roster_hero, unit_build: built_hero });
    const next_build = JSON.parse(JSON.stringify(built_hero));
    next_build.attribute_upgrades = next_build.attribute_upgrades || {};
    const current = next_build.attribute_upgrades[primaryAttTag] || 0;
    const next = Math.max(0, current + direction);
    next_build.attribute_upgrades[primaryAttTag] = next;
    const afterState = getUnitState({ roster_hero, unit_build: next_build });
    const getVal = (state, tag) => state.attributes[tag] ?? state[tag];

    const changes = [];
    for (const secTag of SECONDARY_ATT_TAGS) {
      const beforeVal = getVal(beforeState, secTag);
      const afterVal = getVal(afterState, secTag);
      const delta = afterVal - beforeVal;
      if (!Number.isFinite(delta) || Math.abs(delta) < 0.0001) continue;
      changes.push({
        handle: secTag,
        before: beforeVal,
        after: afterVal,
        delta,
        text: `${text(`attributes.${secTag}.name`)} ${formatSecondaryValue(
          secTag,
          beforeVal
        )} (${formatDelta(secTag, delta)})`,
      });
    }
    return changes;
  }

  function sparkleChangedSecondaryAtts(changes) {
    if (!Array.isArray(changes) || changes.length === 0) return;
    for (const change of changes) {
      const field = _secondaryAttFields?.find((f) => f?.label?.attTag === change.handle);
      if (field?.valueField) {
        CanvasTools.makeFaerieSpinners(field.valueField, 4);
      }
    }
  }

  const destroyPrimaryAttributeFields = () => {
    if (_primaryAttFields) {
      for (const attField of _primaryAttFields) {
        DT_CANVAS_GLOBALS.poolSafeDestroy(attField, { children: true });
      }
      _primaryAttFields = null;
    }
  };

  const destroySecondaryAttributeFields = () => {
    if (_secondaryAttFields) {
      for (const attField of _secondaryAttFields) {
        DT_CANVAS_GLOBALS.poolSafeDestroy(attField, { children: true });
      }
      _secondaryAttFields = null;
    }
  };

  const destroyAttPointsPanel = () => {
    _attPointsPanel?.dispose();
    _attPointsPanel = null;
  };

  // make column titles
  const MIDDLE_GAP = DT_CANVAS_GLOBALS.spacing * 3.5;
  // primary attributes
  var _primaryTitle = new PIXI.Text(text('ui.primary_attributes'), TITLE_STYLE);
  _primaryTitle.x = Math.round(window.innerWidth / 2 - _primaryTitle.width - MIDDLE_GAP);
  _primaryTitle.y = Math.round(window.innerHeight * (window.innerHeight < 700 ? 0.19 : 0.24));
  this.addChild(_primaryTitle);
  // secondary attributes
  var _secondaryTitle = new PIXI.Text(text('ui.secondary_attributes'), TITLE_STYLE);
  _secondaryTitle.x = Math.round(window.innerWidth / 2 + MIDDLE_GAP);
  _secondaryTitle.y = Math.round(_primaryTitle.y);
  this.addChild(_secondaryTitle);

  const ATT_LABEL_STYLE = {
    fontFamily: 'Courier New',
    fontSize: CanvasTools.dynamicFontSizeString(14),
    fill: 0xffffff,
  };
  const ATT_VALUE_STYLE = Object.assign({}, ATT_LABEL_STYLE, {
    fill: Colors[hero_handle],
    fontWeight: 'bold',
    dropShadow: true,
    dropShadowColor: 0x777777,
    dropShadowAngle: Math.PI / 3,
    dropShadowDistance: 1,
  });
  const ATT_FIELD_PADDING = DT_CANVAS_GLOBALS.spacing * 0.75;

  const makePrimaryAttributeField = (attTag) => {
    try {
      var attField = new PIXI.Container();
      var label = new PIXI.Text(text('game.' + attTag) + ':', ATT_LABEL_STYLE);
      label.attTag = attTag;
      label.x = Math.round(ATT_FIELD_PADDING + 25);
      label.y = Math.round(ATT_FIELD_PADDING);
      attField.addChild(label);
      attField.label = label;

      attField.x = Math.round(
        _primaryTitle.x + _primaryTitle.width / 2 - label.width - DT_CANVAS_GLOBALS.spacing
      );
      const field_above =
        _primaryAttFields.length === 0
          ? _primaryTitle
          : _primaryAttFields[_primaryAttFields.length - 1];
      attField.y = Math.round(
        field_above.y + field_above.height + (attTag === 'learning' ? DT_CANVAS_GLOBALS.spacing : 3)
      );

      const atts = _hero_state.attributes;
      const attValue = atts.base[attTag] + atts.fromEquipment[attTag] + atts.fromUpgrades[attTag];

      const buttonSize = 27;
      const lineWidth = 3;
      const lineLength = 14;
      const currentLevel = atts.fromUpgrades[attTag] || 0;

      // downgrade button (red minus sign) - positioned to the left of the label
      if (currentLevel > 0) {
        const db = new PIXI.Graphics();

        // Draw red minus sign
        db.lineStyle(lineWidth, 0xff6666);
        db.moveTo(-lineLength / 2, 0);
        db.lineTo(lineLength / 2, 0);
        db.hitArea = new PIXI.Rectangle(-buttonSize / 2, -buttonSize / 2, buttonSize, buttonSize);

        db.x = label.x - DT_CANVAS_GLOBALS.spacing * 1.5;
        db.y = label.y + label.height / 2;
        attField.addChild(db);
        db.attTag = attTag;
        attField.downgradeButton = db;

        // add downgrade button interactions
        db.interactive = db.buttonMode = true;

        // click - make spinners & send the downgrade request
        db.tap = db.click = (event) => {
          CanvasTools.makeFaerieSpinners(attField.downgradeButton, 4);
          CanvasTools.makeFaerieSpinners(attField.valueField, 4);
          sparkleChangedSecondaryAtts(computeSecondaryChanges(attTag, -1));

          EditHeroActions.downgradeAttribute({
            attribute_handle: event.target.attTag,
            hero_build_id: built_hero.hero_build_id,
            hero_handle,
          });
        };

        db.pointerout = db.mouseout = (event) => {
          event?.stopPropagation?.();
          onAttFieldMouseout(attField);
        };

        // mouseover - brighter red + tooltip with secondary deltas
        db.mouseover = db.pointerover = (event) => {
          event?.stopPropagation?.();
          db.clear();
          db.lineStyle(lineWidth, 0xff8888);
          db.moveTo(-lineLength / 2, 0);
          db.lineTo(lineLength / 2, 0);
          if (_tooltip) _tooltip.dispose();
          const { base, fromUpgrades, fromEquipment } = _hero_state.attributes;
          const cost = Balance.getAttUpgradeCost(base[attTag] + fromUpgrades[attTag]);
          _tooltip = new Tooltip(
            'primary_attribute',
            {
              handle: attTag,
              base: base[attTag],
              fromEquipment: fromEquipment[attTag],
              fromUpgrades: fromUpgrades[attTag],
              cost,
              has_upgrade_cost: _available_att_points >= cost,
              secondary_changes: computeSecondaryChanges(attTag, -1),
            },
            db,
            { forceLeft: true }
          );
        };
        // mouseout - back to normal red
        db.mouseout = db.pointerout = () => {
          db.clear();
          db.lineStyle(lineWidth, 0xff6666);
          db.moveTo(-lineLength / 2, 0);
          db.lineTo(lineLength / 2, 0);

          db.mouseup();
        };
        // mousedown - scale down
        db.mousedown = db.touchstart = () => {
          db.scale_buffer = { x: db.scale.x, y: db.scale.y };
          db.scale = { x: db.scale.x * 0.8, y: db.scale.y * 0.8 };
        };
        // mouseup - scale up
        db.mouseup = db.touchend = () => {
          if (db.scale_buffer) {
            db.scale = db.scale_buffer;
          }
        };
      }

      const vf = (attField.valueField = new PIXI.Text(attValue, ATT_VALUE_STYLE));
      vf.x = Math.round(label.x + label.width + DT_CANVAS_GLOBALS.spacing / 2);
      vf.y = Math.round(label.y + label.height / 2 - vf.height / 2);
      attField.addChild(vf);

      // upgrade button (green plus sign)
      const upgradeCost = Balance.getAttUpgradeCost(atts.base[attTag] + atts.fromUpgrades[attTag]);
      if (_available_att_points >= upgradeCost) {
        const ub = new PIXI.Graphics();

        // Draw green plus sign
        ub.lineStyle(lineWidth, 0x44ff44);
        ub.moveTo(0, -lineLength / 2);
        ub.lineTo(0, lineLength / 2);
        ub.moveTo(-lineLength / 2, 0);
        ub.lineTo(lineLength / 2, 0);
        ub.hitArea = new PIXI.Rectangle(-buttonSize / 2, -buttonSize / 2, buttonSize, buttonSize);

        ub.x = label.x + label.width + DT_CANVAS_GLOBALS.spacing * 4;
        ub.y = label.y + label.height / 2;
        attField.addChild(ub);
        ub.attTag = attTag;
        attField.upgradeButton = ub;

        // add upgrade button interactions
        ub.interactive = ub.buttonMode = true;

        // click - make spinners & send the upgrade request
        ub.tap = ub.click = (event) => {
          CanvasTools.makeFaerieSpinners(attField.upgradeButton, 4);
          CanvasTools.makeFaerieSpinners(attField.valueField, 4);
          sparkleChangedSecondaryAtts(computeSecondaryChanges(attTag, 1));

          const { current_game_mode, current_game_submode } = UIStore.getAll();

          EditHeroActions.upgradeAttribute({
            attribute_handle: event.target.attTag,
            hero_build_id: built_hero.hero_build_id,
            hero_handle,
          });
        };

        ub.pointerout = ub.mouseout = (event) => {
          event?.stopPropagation?.();
          onAttFieldMouseout(attField);
        };

        // mouseover - tint bright yellow + tooltip with secondary deltas
        ub.mouseover = ub.pointerover = (event) => {
          event?.stopPropagation?.();
          ub.clear();
          ub.lineStyle(lineWidth, 0xffff44);
          ub.moveTo(0, -lineLength / 2);
          ub.lineTo(0, lineLength / 2);
          ub.moveTo(-lineLength / 2, 0);
          ub.lineTo(lineLength / 2, 0);
          if (_tooltip) _tooltip.dispose();
          const { base, fromUpgrades, fromEquipment } = _hero_state.attributes;
          const cost = Balance.getAttUpgradeCost(base[attTag] + fromUpgrades[attTag]);
          _tooltip = new Tooltip(
            'primary_attribute',
            {
              handle: attTag,
              base: base[attTag],
              fromEquipment: fromEquipment[attTag],
              fromUpgrades: fromUpgrades[attTag],
              cost,
              has_upgrade_cost: _available_att_points >= cost,
              secondary_changes: computeSecondaryChanges(attTag, 1),
            },
            ub,
            { forceLeft: true }
          );
        };
        // mouseout - back to green
        ub.mouseout = ub.pointerout = () => {
          ub.clear();
          ub.lineStyle(lineWidth, 0x44ff44);
          ub.moveTo(0, -lineLength / 2);
          ub.lineTo(0, lineLength / 2);
          ub.moveTo(-lineLength / 2, 0);
          ub.lineTo(lineLength / 2, 0);

          ub.mouseup();
        };
        // mousedown - scale down
        ub.mousedown = ub.touchstart = () => {
          ub.scale_buffer = { x: ub.scale.x, y: ub.scale.y };
          ub.scale = { x: ub.scale.x * 0.8, y: ub.scale.y * 0.8 };
        };
        // mouseup - scale up
        ub.mouseup = ub.touchend = () => {
          if (ub.scale_buffer) {
            ub.scale = ub.scale_buffer;
          }
        };
      }

      // add invisible backdrop to catch mouse activity anywhere on the line
      attField._width = attField.width + ATT_FIELD_PADDING * 2;
      attField._height = attField.height + ATT_FIELD_PADDING * 2;
      CanvasTools.addBackFill(attField, 0x000000, 0.01);

      // add mouseover tooltip
      attField.interactive = true;
      attField.mouseover = attField.pointerover = onAttFieldMouseover.bind(
        null,
        attField,
        attTag,
        'primary'
      );
      attField.mouseout = attField.pointerout = onAttFieldMouseout.bind(null, attField);

      this.addChild(attField);
      _primaryAttFields.push(attField);
    } catch (err) {
      logError(err, {
        module: 'components/ui_screens/edit_hero/edit_attributes/EditAttributes',
        func: 'makePrimaryAttributeField',
      });
    }
  };

  const onAttFieldMouseover = (attField, attTag, type) => {
    if (_tooltip) {
      _tooltip.dispose();
    }

    if (type === 'primary') {
      const { base, fromUpgrades, fromEquipment } = _hero_state.attributes;
      const cost = Balance.getAttUpgradeCost(base[attTag] + fromUpgrades[attTag]);

      _tooltip = new Tooltip(
        'primary_attribute',
        {
          handle: attTag,
          base: base[attTag],
          fromEquipment: fromEquipment[attTag],
          fromUpgrades: fromUpgrades[attTag],
          cost,
          has_upgrade_cost: _available_att_points >= cost,
          secondary_changes: computeSecondaryChanges(attTag, 1),
        },
        attField,
        { forceLeft: true }
      );
    } else if (type === 'secondary') {
      _tooltip =
        attTag === 'meleeDamage'
          ? new Tooltip(
              'ability',
              {
                ability_handle: 'melee_attack',
                locked: false,
              },
              attField
            )
          : new Tooltip(
              'secondary_attribute',
              {
                handle: attTag,
                value: _hero_state.attributes[attTag],
              },
              attField
            );
    }

    if (!attField.color_buffer) {
      attField.color_buffer = attField.label.style.fill;
    }
    attField.label.style.fill = Colors.BRIGHT_YELLOW;
  };

  const onAttFieldMouseout = (attField) => {
    if (_tooltip) {
      _tooltip.dispose();
      _tooltip = null;
    }

    if (attField.color_buffer) {
      attField.label.style.fill = attField.color_buffer;
    }
  };

  const makePrimaryAttributeFields = () => {
    destroyPrimaryAttributeFields();

    _primaryAttFields = [];
    makePrimaryAttributeField('learning');
    makePrimaryAttributeField('perception');
    makePrimaryAttributeField('mana');
    makePrimaryAttributeField('toughness');
    makePrimaryAttributeField('strength');
    makePrimaryAttributeField('willpower');
    makePrimaryAttributeField('dexterity');
  };

  const makeSecondaryAttributeField = (attTag) => {
    try {
      var attField = new PIXI.Container();
      var label = new PIXI.Text(text('game.' + attTag) + ':', ATT_LABEL_STYLE);
      label.attTag = attTag;
      label.x = Math.round(ATT_FIELD_PADDING);
      label.y = Math.round(ATT_FIELD_PADDING);
      attField.addChild(label);
      attField.label = label;

      attField.x = Math.round(
        _secondaryTitle.x +
          _secondaryTitle.width / 2 -
          attField.width +
          DT_CANVAS_GLOBALS.spacing * 1.5
      );

      const field_above =
        _secondaryAttFields.length === 0
          ? _secondaryTitle
          : _secondaryAttFields[_secondaryAttFields.length - 1];
      attField.y = Math.round(
        field_above.y +
          field_above.height +
          (attTag === 'meleeDamage' ? DT_CANVAS_GLOBALS.spacing : 3)
      );

      var valueText = _hero_state.attributes[attTag] ?? 0;
      switch (attTag) {
        case 'meleeDamage':
          valueText = valueText.toFixed(0);
          break;

        case 'armorClass':
          valueText = `${valueText.toFixed(0)} (${(Balance.getDmgReduxFromArmorClass(valueText) * 100).toFixed(1)}%)`;
          break;

        default:
          valueText = valueText.toFixed(1);
          break;
      }
      if (['dodgeChance', 'physicalResil', 'mentalResil'].includes(attTag)) {
        valueText += '%';
      }

      var valueField = new PIXI.Text(valueText, ATT_VALUE_STYLE);
      valueField.x = Math.round(label.x + label.width + DT_CANVAS_GLOBALS.spacing);
      valueField.y = Math.round(label.y + label.height / 2 - valueField.height / 2);
      attField.addChild(valueField);
      attField.valueField = valueField;

      // add invisible backdrop to catch mouse activity anywhere on the line
      attField._width = attField.width + ATT_FIELD_PADDING * 2;
      attField._height = attField.height + ATT_FIELD_PADDING * 2;
      CanvasTools.addBackFill(attField, 0x000000, 0.01);

      // add mouseover tooltip
      attField.interactive = true;
      attField.mouseover = attField.pointerover = onAttFieldMouseover.bind(
        null,
        attField,
        attTag,
        'secondary'
      );
      attField.mouseout = attField.pointerout = onAttFieldMouseout.bind(null, attField);

      this.addChild(attField);
      _secondaryAttFields.push(attField);
    } catch (err) {
      logError(err, {
        module: 'components/ui_screens/edit_hero/edit_attributes/EditAttributes',
        func: 'makeSecondaryAttributeField',
        attTag,
      });
    }
  };

  const makeSecondaryAttributeFields = () => {
    destroySecondaryAttributeFields();

    _secondaryAttFields = [];
    for (const tag of SECONDARY_ATT_TAGS) {
      makeSecondaryAttributeField(tag);
    }
  };

  const makeAttPointsPanel = () => {
    destroyAttPointsPanel();

    _attPointsPanel = new AttPointsPanel();
    _attPointsPanel.x = Math.round(
      7 + _primaryTitle.x + _primaryTitle.width / 2 - _attPointsPanel.width / 2
    );
    _attPointsPanel.y = Math.round(_primaryTitle.y - _attPointsPanel.height);
    this.addChild(_attPointsPanel);
  };

  // initialization
  makePrimaryAttributeFields();
  makeSecondaryAttributeFields();
  makeAttPointsPanel();

  const onAttributeChange = () => {
    try {
      const heroBuffer = JSON.parse(JSON.stringify(_hero_state));
      const { built_hero, roster_hero } = FluxGetters.getFocusedHeroBuild();
      _available_att_points = getAvailableAttributePoints({
        built_hero,
        roster_hero,
      });
      _hero_state = getUnitState({
        roster_hero,
        unit_build: built_hero,
      });

      // check for attribute level-ups & play a sound
      for (let prop in _hero_state.attributes.fromUpgrades) {
        if (_hero_state.attributes.fromUpgrades[prop] > heroBuffer.attributes.fromUpgrades[prop]) {
          Audio.play('star_twinkle');

          if (_tooltip) {
            _tooltip.dispose();
            _tooltip = null;
          }
          break;
        }
      }

      // check for reset && play a sound
      var allLevelZero = true;
      for (let prop in _hero_state.attributes.fromUpgrades) {
        if (_hero_state.attributes.fromUpgrades[prop] > 0) {
          allLevelZero = false;
          break;
        }
      }
      if (allLevelZero) {
        Audio.play('electric_smash');
        if (_tooltip) {
          _tooltip.dispose();
          _tooltip = null;
        }
      }

      makePrimaryAttributeFields();
      makeSecondaryAttributeFields();
      makeAttPointsPanel();
    } catch (err) {
      logError(err, {
        module: 'components/ui_screens/edit_hero/edit_attributes',
        func: 'onAttributeChange',
      });
    }
  };
  HeroBuildStore.on(HeroBuildStore.ATTRIBUTE_CHANGE, onAttributeChange);
  UIStore.on(UIStore.GAME_SUBMODE_SELECTION, onAttributeChange);
};
EditAttributes.prototype = Object.create(PIXI.Container.prototype);
EditAttributes.prototype.constructor = EditAttributes;
export default EditAttributes;
